import React, { useState } from "react";
import { FormattedNumber, FormattedMessage } from "react-intl";
import dayjs from "dayjs";
import { Grid, Box, Paper, MenuItem, makeStyles } from "@material-ui/core";
import View from "components/utils/View";
import { REGISTER_WITH_CODE_ROUTE_REDIRECT } from "routes/auth/authPaths";
import useDistributors from "./useDistributors";
import { DISTRIBUTOR_TYPE } from "./constants";
import DistributorsTable from "./DistributorsTable";
import {
  DateRange,
  SrCard,
  Typography,
  CopyButton,
  SrContentHeader,
  SrContentWrapper,
  SrFilterSelect,
  SrCurrency,
} from "../../../common";
import translate from "../../../../i18n/translate";

const BASE_URL = process.env.REACT_APP_HOST;

export const useStyles = makeStyles(() => ({
  link: {
    lineBreak: "anywhere",
  },
}));

const Distributors = () => {
  const classes = useStyles();
  const [range, setRange] = useState({
    startDate: dayjs().startOf("month"),
    endDate: dayjs(),
  });
  const {
    loading,
    paginationConfig,
    distributors,
    code,
    onCodeSelectorChange,
    codes,
    userType,
    dashboard: { readers, transacted, distributor, goalPrecentage },
  } = useDistributors(range);

  const referalsLink =
    BASE_URL + REGISTER_WITH_CODE_ROUTE_REDIRECT.replace(":code", code);

  return (
    <>
      <View translateTitleKey="menu.distributors" />
      <Grid container direction="column" spacing={2}>
        <SrContentHeader
          title={<FormattedMessage id="menu.distributors" />}
          subTitle={<FormattedMessage id="distributors.subtitle" />}
          actions={
            <>
              {code && (
                <SrFilterSelect
                  select
                  size="small"
                  fullWidth
                  id="code"
                  name="code"
                  onChange={onCodeSelectorChange}
                  error={false}
                  value={code}
                >
                  {codes.map((item) => (
                    <MenuItem key={item.code} value={item.code}>
                      {item.code}
                    </MenuItem>
                  ))}
                </SrFilterSelect>
              )}
              <DateRange
                monthSelector
                onDateSelected={({ startDate, endDate }) => {
                  setRange({ startDate, endDate });
                }}
                minDate="2020-01-01"
              />
            </>
          }
        />

        <SrContentWrapper isLoading={loading}>
          <Grid container justifyContent="center" spacing={2}>
            <Grid container item xs={12} spacing={2}>
              <Box paddingLeft={1}>
                <Typography variant="subtitle1">
                  <FormattedMessage id="distributors.link" />
                </Typography>
                <Typography color="primary" className={classes.link}>
                  {referalsLink}
                  <CopyButton color="default" copy={referalsLink} />
                </Typography>
              </Box>
            </Grid>
            <Grid container item xs={12} spacing={2}>
              <Grid item xs={12} sm={6} lg>
                <SrCard
                  title={translate(
                    `distributors.${
                      userType === DISTRIBUTOR_TYPE.master
                        ? "readers"
                        : "benefits"
                    }`
                  )}
                  titleId="readers"
                  loading={loading}
                  value={<FormattedNumber value={readers} />}
                  detail={
                    userType === DISTRIBUTOR_TYPE.master ? undefined : (
                      <FormattedMessage
                        id="distributors.nextLevel"
                        values={{ percentage: goalPrecentage }}
                      />
                    )
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} lg>
                <SrCard
                  title={translate("distributors.transacted")}
                  titleId="transacted"
                  loading={loading}
                  value={<SrCurrency value={transacted} />}
                />
              </Grid>
              <Grid item xs={12} sm={12} lg>
                <SrCard
                  title={translate("distributors.distributor")}
                  titleId="distributor"
                  loading={loading}
                  value={<SrCurrency value={distributor} />}
                />
              </Grid>
            </Grid>

            <Grid container item xs={12} spacing={2}>
              <Grid item xs={12}>
                <Paper elevation={0}>
                  <DistributorsTable
                    distributors={distributors}
                    pagination={paginationConfig}
                    loading={loading}
                    userType={userType}
                  />
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </SrContentWrapper>
      </Grid>
    </>
  );
};

export default Distributors;
